import { useState, useEffect, useCallback } from 'react';
import { orderService } from '../services/OrderService';
import useAlert from '../../../shared/hooks/useAlert';
import { usePagination } from '../../../shared/hooks/usePagination';

export const useOrder = () => {
  const { alert, showAlert } = useAlert();

  // Gunakan hook pagination dari utils
  const {
    pagination,
    data,
    meta,
    links,
    loading,
    error,
    setData,
    setLoading,
    setError,
    setMeta,
    setLinks,
    updateFromResponse,
    updatePagination,
    goToPage,
    changeLimit,
    changeSort,
    toggleSortOrder,
    search,
    resetPagination,
    buildQueryParams,
    getQueryObject: getBaseQueryObject,
    getSortInfo,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    currentSortField,
    currentSortOrder,
  } = usePagination();

  // State untuk filters (sesuaikan dengan kebutuhan)
  const [filters, setFilters] = useState({
    category: 'ALL',        // 'ALL', 'SERVICE', 'APPOINTMENT', 'PRODUCT'
    paymentStatus: 'ALL',    // 'ALL', 'PAYMENT SUCCESS', 'NEED PAYMENT', 'PAYMENT FAILED'
    xenditStatus: 'ALL',     // 'ALL', 'PAID', 'PENDING'
  });

  const orders = data;

  const getQueryObject = useCallback(() => {
    const baseQuery = getBaseQueryObject();

    const queryWithFilters = {
      ...baseQuery,
      ...(filters.category !== 'ALL' && { TypeProduct: filters.category }),
      ...(filters.paymentStatus !== 'ALL' && { payment_status: filters.paymentStatus }),
      ...(filters.xenditStatus !== 'ALL' && { xendit_status: filters.xenditStatus }),
    };

    return queryWithFilters;
  }, [getBaseQueryObject, filters]);

  // Bangun query string untuk API
  const buildQueryParamsWithFilters = useCallback(() => {
    const queryObject = getQueryObject();
    const params = new URLSearchParams();

    Object.entries(queryObject).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    return params.toString();
  }, [getQueryObject]);

  // Fetch orders dengan pagination dan filter
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryObject = getQueryObject();

      const result = await orderService.getPaginateAdmin(queryObject);

      if (result.success) {
        updateFromResponse(result.data);
      } else {
        console.error('❌ API Error:', result.error);
        setError(result.error);
        showAlert(result.error, 'danger');
      }
    } catch (err) {
      console.error('🚨 Network Error:', err);
      const errorMessage = 'Network error occurred';
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
    } finally {
      setLoading(false);
    }
  }, [getQueryObject, filters, setLoading, setError, showAlert, updateFromResponse]);

  /**
   * 🎯 BARU: Fetch SEMUA data untuk export Excel
   * - Loop semua halaman di orderService.getAllPaginateAdmin
   * - TIDAK mengubah state pagination tabel
   * - Return array of orders (atau throw error)
   */
  const fetchAllForExport = useCallback(async () => {
    const result = await orderService.getAllPaginateAdmin({});
    if (result.success) {
      return result.data || [];
    }
    throw new Error(result.error || 'Gagal mengambil semua data');
  }, []);

  // Update filter functions
  const updateCategoryFilter = useCallback((category) => {
    setFilters(prev => ({ ...prev, category }));
    goToPage(1);
  }, [goToPage]);

  const updatePaymentStatusFilter = useCallback((paymentStatus) => {
    setFilters(prev => ({ ...prev, paymentStatus }));
    goToPage(1);
  }, [goToPage]);

  const updateXenditStatusFilter = useCallback((xenditStatus) => {
    setFilters(prev => ({ ...prev, xenditStatus }));
    goToPage(1);
  }, [goToPage]);

  const clearFilters = useCallback(() => {
    setFilters({
      category: 'ALL',
      paymentStatus: 'ALL',
      xenditStatus: 'ALL',
    });
    goToPage(1);
    search('');
  }, [goToPage, search]);

  // Fetch data saat pagination atau filters berubah
  useEffect(() => {
    fetchOrders();
  }, [
    pagination.page,
    pagination.limit,
    pagination.sortBy,
    pagination.search,
    filters.category,
    filters.paymentStatus,
    filters.xenditStatus,
  ]);

  return {
    // Data state
    orders,
    loading,
    error,
    alert,

    // Pagination state
    pagination,
    meta,
    links,

    // Filter state
    filters,

    // Pagination actions
    fetchOrders,
    goToPage,
    changeLimit,
    changeSort,
    toggleSortOrder,
    search,
    resetPagination,

    // Filter actions
    updateCategoryFilter,
    updatePaymentStatusFilter,
    updateXenditStatusFilter,
    clearFilters,

    // Pagination info
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    currentSortField,
    currentSortOrder,

    // 🎯 Export
    fetchAllForExport,

    // Utilities
    buildQueryParams: buildQueryParamsWithFilters,
    getQueryObject,
    getSortInfo,
  };
};